import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { startOfMonth, endOfMonth } from "date-fns";
import {
  AdminDataTable,
  type ColumnDef,
  DateRangePicker,
  type DateRange,
  FilterBar,
  type FilterConfig,
  EmptyState,
  ExportButton,
} from "@/components/admin/shared";
import { MovementTypeBadge } from "./MovementTypeBadge";
import { useStockMovements } from "@/hooks/admin/useInventory";
import type { StockMovement, MovementType } from "@/lib/admin-api/inventory";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

const TYPE_OPTIONS = [
  { value: "sale", label: "Vente" },
  { value: "return", label: "Retour" },
  { value: "restock", label: "Réapprovisionnement" },
  { value: "adjustment", label: "Ajustement" },
  { value: "damage", label: "Dommage" },
] as const;

const FILTER_CONFIGS: readonly FilterConfig[] = [
  {
    key: "type",
    label: "Type",
    type: "select",
    options: TYPE_OPTIONS,
  },
];

const EXPORT_COLUMNS = [
  { key: "created_at", label: "Date" },
  { key: "product_name", label: "Produit" },
  { key: "product_sku", label: "SKU" },
  { key: "type", label: "Type" },
  { key: "quantity", label: "Quantité" },
  { key: "reason", label: "Raison" },
  { key: "operator", label: "Opérateur" },
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MovementsTab() {
  const { t } = useTranslation("admin");

  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<MovementType | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  });

  const queryParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      type: typeFilter,
      date_from: format(dateRange.from, "yyyy-MM-dd"),
      date_to: format(dateRange.to, "yyyy-MM-dd"),
    }),
    [page, typeFilter, dateRange],
  );

  const { data, isLoading, isError } = useStockMovements(queryParams);

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    if (key === "type") {
      setTypeFilter((value as MovementType | null) ?? null);
    }
    setPage(1);
  }, []);

  const handleDateChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setPage(1);
  }, []);

  const movements = useMemo(() => data?.movements ?? [], [data?.movements]);
  const pagination = data?.pagination;

  const exportData = useMemo(
    () =>
      movements.map((m) => ({
        created_at: format(new Date(m.created_at), "dd/MM/yyyy HH:mm", {
          locale: fr,
        }),
        product_name: m.product_name,
        product_sku: m.product_sku,
        type: m.type,
        quantity: m.quantity,
        reason: m.reason,
        operator: m.operator,
      })),
    [movements],
  );

  const isPositiveMovement = (type: MovementType) =>
    type === "restock" || type === "return";

  const columns: readonly ColumnDef<StockMovement>[] = useMemo(
    () => [
      {
        id: "date",
        header: t("inventory.col_date", "Date"),
        cell: (m) => (
          <span className="text-xs text-stone-500">
            {format(new Date(m.created_at), "dd MMM yyyy HH:mm", {
              locale: fr,
            })}
          </span>
        ),
      },
      {
        id: "product",
        header: t("inventory.col_product", "Produit"),
        cell: (m) => (
          <span>
            <span className="block text-sm font-medium text-stone-800">
              {m.product_name}
            </span>
            <span className="font-mono text-xs text-stone-400">
              {m.product_sku}
            </span>
          </span>
        ),
      },
      {
        id: "type",
        header: t("inventory.col_type", "Type"),
        cell: (m) => <MovementTypeBadge type={m.type} />,
      },
      {
        id: "quantity",
        header: t("inventory.col_quantity", "Quantité"),
        sortable: true,
        cell: (m) => (
          <span
            className={`tabular-nums font-semibold ${
              isPositiveMovement(m.type)
                ? "text-emerald-700"
                : "text-red-600"
            }`}
          >
            {isPositiveMovement(m.type) ? "+" : "-"}
            {Math.abs(m.quantity)}
          </span>
        ),
      },
      {
        id: "reason",
        header: t("inventory.col_reason", "Raison"),
        mobileVisible: false,
        cell: (m) => (
          <span className="text-sm text-stone-600">{m.reason}</span>
        ),
      },
      {
        id: "operator",
        header: t("inventory.col_operator", "Opérateur"),
        mobileVisible: false,
        cell: (m) => (
          <span className="text-xs text-stone-500">{m.operator}</span>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <DateRangePicker value={dateRange} onChange={handleDateChange} />
          <FilterBar
            filters={FILTER_CONFIGS}
            values={{ type: typeFilter }}
            onChange={handleFilterChange}
          />
        </div>
        <ExportButton
          data={exportData}
          columns={[...EXPORT_COLUMNS]}
          filename="mouvements-stock-djayma"
        />
      </div>

      {/* Error */}
      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {t(
            "inventory.error_movements",
            "Impossible de charger les mouvements.",
          )}
        </div>
      )}

      {/* Table */}
      {!isLoading && movements.length === 0 ? (
        <EmptyState
          title={t("inventory.no_movements", "Aucun mouvement")}
          description={t(
            "inventory.no_movements_desc",
            "Aucun mouvement enregistré pour la période sélectionnée.",
          )}
        />
      ) : (
        <AdminDataTable<StockMovement>
          columns={columns}
          data={movements}
          isLoading={isLoading}
          pagination={pagination}
          onPageChange={setPage}
          emptyMessage={t("inventory.no_movements", "Aucun mouvement")}
        />
      )}
    </div>
  );
}
